import React from 'react';

import { PackageCategory, VulnerabilitySeverity } from '@ariksa/inventory-core';
import { NativeResources, Resources } from '@ariksa/inventory-core/api';
import { Box } from '@chakra-ui/react';
import { map } from 'lodash';
import { ApplicationEnum, LibraryType } from 'services/Vulnerability/types';
import { customTheme } from 'theme';

import { renderSeverityBar } from 'components/DataDisplay';
import { ButtonGroupItem } from 'components/DataEntry';
import {
  BarChartIcon,
  IconTypes,
  LineChartIcon,
  SummaryIcon,
  TechnologyIcon,
} from 'components/Icons';
import { getIcon } from 'components/Icons/Components';
import { toTitleCase } from 'utils/string';

export const vulnerabilityResourceTypeList = [
  {
    label: 'RESOURCE',
    options: [
      {
        label: 'Virtual Machines',
        value: Resources.VirtualMachine,
        icon: getIcon(Resources.VirtualMachine),
      },
      {
        label: 'Containers',
        value: NativeResources.ElasticContainerService,
        icon: getIcon(NativeResources.Container),
      },
      /*{
        label: 'Virtual Machine Containers',
        value: CONTAINER_ON_VIRTUAL_MACHINE,
        icon: getIcon(IconTypes.ContainersOnVirtualMachine),
      },*/
      {
        label: 'Machine Images',
        value: Resources.Image,
        icon: getIcon(IconTypes.MachineImage),
      },
      {
        label: 'Container Images',
        value: NativeResources.ElasticContainerRegistry,
        icon: getIcon(NativeResources.ElasticContainerRegistry),
      },
      {
        label: 'Serverless Functions',
        value: Resources.ServerlessCompute,
        icon: getIcon(NativeResources.LambdaFunction),
      },
    ],
  },
];

export const viewVulnerabilityByItems: ButtonGroupItem[] = [
  {
    label: 'Resource',
    value: 'resource',
  },
  {
    label: 'CVE',
    value: 'cve',
  },
  {
    label: 'Package',
    value: 'package',
  },
];

export const vulnerabilitySeverityOptions = [
  { label: 'Severity', value: undefined },
  ...map(VulnerabilitySeverity, o => ({
    value: o,
    label: toTitleCase(o),
    icon: renderSeverityBar({ value: o, showLabel: false }),
  })),
];

const renderBoolean = value => (
  <Box boxSize={3} bg={value ? 'green.300' : 'critical'} borderRadius={3} />
);

export const patchAvailableOptions = [
  { label: 'Has Patch', value: undefined },
  { label: 'true', value: 'yes', icon: renderBoolean(true) },
  { label: 'false', value: 'no', icon: renderBoolean(false) },
];

export const exploitableOptions = [
  { label: 'Has Public Exploit', value: undefined },
  { label: 'true', value: 'yes', icon: renderBoolean(false) },
  { label: 'false', value: 'no', icon: renderBoolean(true) },
];

export const internetExposureOptions = [
  { label: 'Internet Exposure', value: undefined },
  { label: 'true', value: 'yes', icon: renderBoolean(false) },
  { label: 'false', value: 'no', icon: renderBoolean(true) },
];

export const hasHighPrivilegesOptions = [
  { label: 'Has High Privileges', value: undefined },
  { label: 'true', value: 'yes', icon: renderBoolean(false) },
  { label: 'false', value: 'no', icon: renderBoolean(true) },
];

export const internetAccessOptions = [
  { label: 'Internet Exposure', value: undefined },
  { label: 'true', value: 'yes' },
  { label: 'false', value: 'no' },
];

export const statusOptions = [
  { label: 'Open', value: false, icon: renderBoolean(false) },
  { label: 'Resolved', value: true, icon: renderBoolean(true) },
];

export const packageTypeOptions = selectedTab => [
  { label: 'Package type', value: undefined },
  ...(selectedTab !== NativeResources.ElasticContainerRegistry
    ? [
        {
          label: 'OS',
          value: PackageCategory.OsPackage,
        },
        {
          label: 'Library',
          value: PackageCategory.LibraryPackage,
        },
      ]
    : []),
  {
    label: 'Technology',
    value: PackageCategory.ApplicationPackage,
  },
];

// Package type is a PackageCategory value (from the URL or packageTypeOptions).
export const packageNameOptions = (packageType?: string) => {
  switch (packageType) {
    case PackageCategory.OsPackage:
      return osOptions;
    case PackageCategory.ApplicationPackage:
      return applicationOptions;
    case PackageCategory.LibraryPackage:
      return libraryOptions;
    default:
      return [];
  }
};

export const libraryOptions = [
  {
    label: 'All Libraries',
    value: undefined,
  },
  ...map(LibraryType, o => ({
    label: o,
    value: o,
  })),
];

export const applicationOptions = [
  {
    label: 'All Applications',
    value: undefined,
  },
  ...map(ApplicationEnum, o => ({
    label: o,
    value: o,
  })),
];

export const osOptions = [
  {
    label: 'All OS',
    value: undefined,
  },
  {
    label: 'Ubuntu',
    value: 'Ubuntu',
  },
  {
    label: 'Amazon Linux',
    value: 'Amazon Linux',
  },
];

export const showOptions = [
  { label: 'Summary', value: 'Summary', icon: <SummaryIcon /> },
  { label: 'Details', value: 'Details', icon: <TechnologyIcon /> },
];

export const vulnerabilityChartColorMap = {
  Critical: customTheme.colors.critical,
  High: customTheme.colors.high,
  Medium: customTheme.colors.medium,
  Low: customTheme.colors.low,
  Unknown: customTheme.colors.gray['200'],
};

export const vulnerabilityChartItems = [
  { tooltip: 'Cumulative', value: 'line', icon: <LineChartIcon /> },
  { tooltip: 'Per Day', value: 'bar', icon: <BarChartIcon /> },
];
